import React, { useMemo } from 'react';
import classnames from 'classnames/bind';

import { AdminSingleSectionDataVariant } from '@INTERFACES/admin-api/sections';
import { formatPropertiesList } from '@HELPERS/formatPropertiesList';
import { attachmentsAdminApi } from '@SERVICES';

import styles from './VariantListCard.module.scss';

const cx: CX = classnames.bind(styles);

interface Props {
    variant: AdminSingleSectionDataVariant & { isDefault?: boolean };
    isSelected: boolean;
    onClick: () => void;
}

function VariantListCard(props: Props) {
    const { variant, isSelected, onClick } = props;

    // Get attachment count
    const { data: attachmentsData } = attachmentsAdminApi.useGetAttachmentsQuery({
        content_type: 'variant',
        object_id: variant.id,
    });
    const attachmentCount = attachmentsData?.length || 0;

    const variantProperties = useMemo(() => {
        const properties = [];

        if (variant.brands?.length > 0) {
            properties.push({
                type: 'brands',
                label: 'Brand',
                value: formatPropertiesList(variant.brands),
                count: variant.brands.length,
            });
        }

        if (variant.regions?.length > 0) {
            properties.push({
                type: 'regions',
                label: 'Region',
                value: formatPropertiesList(variant.regions),
                count: variant.regions.length,
            });
        }

        if (variant.areas?.length > 0) {
            properties.push({
                type: 'areas',
                label: 'Area',
                value: formatPropertiesList(variant.areas),
                count: variant.areas.length,
            });
        }

        if (variant.countries?.length > 0) {
            properties.push({
                type: 'countries',
                label: 'Country',
                value: formatPropertiesList(variant.countries),
                count: variant.countries.length,
            });
        }

        if (variant.propertyTypes?.length > 0) {
            properties.push({
                type: 'propertyTypes',
                label: 'Type',
                value: formatPropertiesList(variant.propertyTypes),
                count: variant.propertyTypes.length,
            });
        }

        return properties;
    }, [variant]);

    const getStatusInfo = () => {
        if (!variant.isPublished) {
            return { text: 'Not Published', className: 'not-published' };
        }
        
        const hasUnpublishedChanges = variant.versions?.length === 0
            || (variant.body !== variant.versions[variant.versions.length - 1]?.body);
        
        if (hasUnpublishedChanges) {
            return { text: 'Has Changes', className: 'has-changes' };
        }
        
        return { text: 'Published', className: 'published' };
    };

    const statusInfo = getStatusInfo();

    return (
        <div
            className={cx('variant-list-card', {
                'is-selected': isSelected,
                'is-default': variant.isDefault,
            })}
            onClick={onClick}
            role="button"
            tabIndex={0}
        >
            <div className={cx('card-header')}>
                <div className={cx('variant-info')}>
                    <h4 className={cx('variant-name')}>
                        {variant.isDefault ? 'Default Variant' : (variant.name || `Variant ${variant.id}`)}
                    </h4>
                    <div className={cx('status-badge', statusInfo.className)}>
                        {statusInfo.text}
                    </div>
                </div>
                
                {variant.isDefault && (
                    <div className={cx('default-badge')}>
                        Default
                    </div>
                )}
            </div>

            {!variant.isDefault && variantProperties.length > 0 && (
                <div className={cx('properties-list')}>
                    {variantProperties.map((property, index) => (
                        <div key={property.type} className={cx('property-badge')}>
                            <span className={cx('property-label')}>
                                {property.label}
                                :
                            </span>
                            <span className={cx('property-value')}>{property.value}</span>
                        </div>
                    ))}
                </div>
            )}

            {variant.tags && variant.tags.length > 0 && (
                <div className={cx('tags-section')}>
                    <div className={cx('tags-list')}>
                        {variant.tags.slice(0, 3).map((tag) => (
                            <span key={tag.id} className={cx('tag-chip')}>
                                {tag.name}
                            </span>
                        ))}
                        {variant.tags.length > 3 && (
                            <span className={cx('tag-chip', 'more-tags')}>
                                +
                                {variant.tags.length - 3}
                                {' '}
                                more
                            </span>
                        )}
                    </div>
                </div>
            )}

            <div className={cx('card-footer')}>
                <div className={cx('meta-info')}>
                    {variant.vendors && variant.vendors.length > 0 && (
                        <span className={cx('vendor-count')}>
                            {variant.vendors.length}
                            {' '}
                            vendor
                            {variant.vendors.length !== 1 ? 's' : ''}
                        </span>
                    )}
                    {attachmentCount > 0 && (
                        <span className={cx('attachment-count')}>
                            {attachmentCount}
                            {' '}
                            attachment
                            {attachmentCount !== 1 ? 's' : ''}
                        </span>
                    )}
                    {variant.versions && variant.versions.length > 0 && (
                        <span className={cx('version-count')}>
                            v
                            {variant.versions.length}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VariantListCard; 